/**
 * Route wrapper for creating a new proposal.
 *
 * Default behaviour: shows the AuthorityTypeDialog, then renders ProposalForm
 * after type selection.
 *
 * CZNA-241 — two-way gate for the propose-GA-from-picker flow:
 *
 *  OUTBOUND (child GA create): the parent form's picker navigates here with
 *  `?type=GEOGRAPHIC&seed=<text>&flow=<token>`. With `type` present the dialog
 *  is skipped and ProposalForm renders directly, seeded with `seed`. On create
 *  success we `attachResult(flow, …)` and navigate back to the parent's
 *  `returnTo` (read from the snapshot) with `restore=<flow>` appended; on cancel
 *  we navigate back WITHOUT a result (no write-back).
 *
 *  INBOUND (parent restore): when the parent is itself a CREATE-mode proposal,
 *  the GA route returns here with `?type=<parentType>&restore=<token>`. With
 *  `type` set the dialog is skipped (same as outbound) and `restore` is threaded
 *  down to ProposalForm → RecordForm to rehydrate the parent and write back.
 *
 * Plain `/create` (no query) is unchanged — the AuthorityTypeDialog is shown.
 *
 * CZNA-321 / CZNA-320 — for a {@link PUBLISHING_PROPOSE_TARGETS} type (PERSONAL, CORPORATE, MEETING) the child is a
 * "minimal create": the same form-config narrowed to {@link MINIMAL_CREATE_TABS}, and
 * it PUBLISHES rather than saves a draft, so the parent gets back the published
 * RECORD id, never a draft's. GEOGRAPHIC keeps its CZNA-241 draft behaviour.
 *
 * CZNA-591 — the route is also the permission gate. `ProposalsList` hides its
 * "nový návrh" button behind `canCreateProposal`, but hiding a button is not a
 * gate: this route is reachable by URL, by the empty-DRAFT call to action, and
 * by the picker's propose-GA flow. A user without the role used to get the whole
 * form and learn the truth only on save, as a 403 rendered "zkuste to znovu".
 *
 * CZNA-598 — and the predicate is no longer computed here. `canCreateProposalOfType`
 * now reads `GET /czna-authorities/proposals/allowed-actions`, which the backend fills
 * from the same `PermissionMatrix` that `POST /proposals` enforces
 * (`ProposalActionResolver#creatableTypes`), scoped to the CALLER'S OWN organization
 * exactly as `ProposalServiceImpl#createProposal` scopes it.
 *
 * That closes the residual gap this comment used to record — a permission row scoped to
 * an organization OTHER than the user's own, where the frontend said yes and the backend
 * answered 403 — because the answer is now the backend's own.
 *
 * The gate is per TYPE, not per user: `canCreateProposal` only says the user may start
 * SOMETHING, so a cataloguer for PERSONAL could still walk into a CORPORATE form.
 *
 * Do NOT reintroduce a locally-computed predicate here, in either direction. The
 * creation rule is NOT the edit rule (there is no proposal yet, so no owning
 * organization to compare against) and it is NOT the national-only rule either — that
 * prescription predates CZNA-600 and would deny creation to every org-scoped
 * cataloguer, the outage CZNA-600 removed and the CZNA-561 personas measured.
 */
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import AuthorityTypeDialog from '../components/ProposalForm/AuthorityTypeDialog';
// From its own module, not via the dialog: a test that stubs the dialog must not
// also get to redefine the list this route filters. See authorityTypeOrder.ts.
import { TYPE_ORDER } from '../components/ProposalForm/authorityTypeOrder';
import ProposalForm from '../components/ProposalForm/ProposalForm';
import { peekSnapshot, attachResult, MINIMAL_CREATE_TABS, PUBLISHING_PROPOSE_TARGETS, } from '../components/ProposalForm/proposeAuthorityFlow';
import { ProposalGateLoading, ProposalGateMessage } from '../components/ProposalGate';
import useAllowedProposalActions from '../hooks/useAllowedProposalActions';
var BACK_LABEL = 'ui-czna-authorities.proposals.create.denied.back';
var DRAFTS_WORKSPACE = '/czna-authorities/authorities/my-drafts';
var ProposalCreate = function () {
    var _a, _b, _c, _d;
    var intl = useIntl();
    var history = useHistory();
    var location = useLocation();
    var params = new URLSearchParams(location.search);
    // CZNA-598 — the backend now says which types this user may start a proposal for; the
    // frontend no longer derives it from raw permission rows. Same rules, one evaluation,
    // and the create dialog cannot offer a type POST /proposals would refuse.
    var _e = useAllowedProposalActions(), isAuthorizationLoading = _e.isLoading, isAuthorizationError = _e.isError, canCreateProposalOfType = _e.canCreateProposalOfType;
    // The types this user may actually start a proposal for. Derived from the same
    // predicate the backend enforces, over the same TYPE_ORDER the dialog renders,
    // so "what is offered" and "what is accepted" cannot disagree.
    var creatableTypes = TYPE_ORDER.filter(canCreateProposalOfType);
    var typeParam = params.get('type');
    var seedParam = (_a = params.get('seed')) !== null && _a !== void 0 ? _a : undefined;
    var flowToken = (_b = params.get('flow')) !== null && _b !== void 0 ? _b : undefined; // child GA create flow
    var restoreToken = (_c = params.get('restore')) !== null && _c !== void 0 ? _c : undefined; // parent restore mount
    // `type` in the URL means we already know the record type (either the child GA
    // flow with type=GEOGRAPHIC, or a parent restore that re-passes its own type),
    // so the dialog is skipped. Manual `/create` has no `type` and falls back to
    // the dialog.
    //
    // CZNA-241 fix: the effective type MUST be derived from the URL on every render,
    // never latched into state. The CREATE parent form and the child GA form mount
    // through the SAME route (`/proposals/create`), so a picker-driven
    // `history.push('?type=GEOGRAPHIC&…')` from a PERSONAL/CORPORATE/MEETING parent
    // reconciles the SAME mounted ProposalCreate instance — a `useState(typeParam)`
    // value would stay the parent's type and the GA form would render (and save) as
    // the wrong type. State holds only the manual-dialog selection.
    var _f = useState(null), dialogType = _f[0], setDialogType = _f[1];
    var selectedType = typeParam !== null && typeParam !== void 0 ? typeParam : dialogType; // URL wins, always fresh
    /**
     * CZNA-456 — the template the cataloguer picked in the dialog, remembered
     * TOGETHER WITH the type it was picked for.
     *
     * The pairing is not decoration. The parent form and the child GA form mount
     * through this SAME route and reconcile the SAME component instance (see the
     * `dialogType` comment above), so a bare `templateId` in state would leak from
     * a PERSONAL parent onto the GEOGRAPHIC child — a template id is scoped to one
     * authority type and means nothing under another. Keyed by type, it survives
     * the picker round trip for the parent and is correctly ignored for the child.
     */
    var _g = useState(null), dialogTemplate = _g[0], setDialogTemplate = _g[1];
    // An explicit `?template=` wins, mirroring how `?type=` wins for the type.
    var templateParam = (_d = params.get('template')) !== null && _d !== void 0 ? _d : undefined;
    var selectedTemplateId = templateParam !== null && templateParam !== void 0 ? templateParam : (dialogTemplate && dialogTemplate.type === selectedType
        ? dialogTemplate.templateId
        : undefined);
    var handleTypeSelect = function (type, templateId) {
        setDialogType(type);
        setDialogTemplate(templateId ? { type: type, templateId: templateId } : null);
    };
    // Append a query string to the parent returnTo URL, merging cleanly whether or
    // not the base already has a `?`.
    var withRestore = function (returnTo, token) {
        var sep = returnTo.includes('?') ? '&' : '?';
        return "".concat(returnTo).concat(sep, "restore=").concat(token);
    };
    var handleCancel = function () {
        // Child GA flow: a cancel returns to the parent WITHOUT attaching a result —
        // the parent restores its in-progress state but leaves the 370 subfield
        // untouched. We still pass `restore=<token>` so the parent rehydrates.
        if (flowToken) {
            var snapshot = peekSnapshot(flowToken);
            if (snapshot) {
                history.push(withRestore(snapshot.returnTo, flowToken));
                return;
            }
        }
        history.push(DRAFTS_WORKSPACE);
    };
    // Child GA flow: on successful create, attach the new GA's id + heading to the
    // snapshot and navigate back to the parent with the restore token so it writes
    // the FK + heading into the originating 370 subfield.
    var handleCreated = function (proposal) {
        var _a;
        if (!flowToken) {
            // Defensive — `onCreated` is only wired when flowToken is present.
            history.push(DRAFTS_WORKSPACE);
            return;
        }
        // CZNA-321: a publishing child hands back the proposal AFTER publish, so the
        // reference is the record's. The proposal id is the CZNA-241 draft case only.
        attachResult(flowToken, {
            id: (_a = proposal.publishedRecordId) !== null && _a !== void 0 ? _a : proposal.id,
            heading: proposal.headingValue,
        });
        var snapshot = peekSnapshot(flowToken);
        history.push(snapshot ? withRestore(snapshot.returnTo, flowToken) : DRAFTS_WORKSPACE);
    };
    /**
     * A full-pane message where the form would have been, with a way back.
     *
     * CZNA-602 — the chrome now lives in `components/ProposalGate`, shared with the edit
     * route. Both exits go through handleCancel, NOT straight to the list: see the
     * `onClose` doc there for why.
     */
    var renderMessagePane = function (testId, heading, body) { return (React.createElement(ProposalGateMessage, { testId: testId, heading: heading, body: body, backLabelId: BACK_LABEL, onClose: handleCancel })); };
    /**
     * Explains what the user is missing instead of handing them a form the server
     * will refuse. `type` is named whenever we know it — "nemáte oprávnění" without
     * saying to WHAT leaves the user with nothing to ask the administrator for.
     */
    var renderDenied = function (type) { return renderMessagePane('proposal-create-denied', React.createElement(FormattedMessage, { id: type
            ? 'ui-czna-authorities.proposals.create.denied.type.heading'
            : 'ui-czna-authorities.proposals.create.denied.any.heading' }), type ? (React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.create.denied.type.message", values: {
            type: intl.formatMessage({ id: "ui-czna-authorities.recordType.".concat(type) }),
        } })) : (React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.create.denied.any.message" }))); };
    /** The permissions could not be read — say that, rather than blaming the user. */
    var renderUnavailable = function () { return renderMessagePane('proposal-create-unavailable', React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.create.unavailable.heading" }), React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.create.unavailable.message" })); };
    // The authorization context arrives asynchronously and reads as "no roles"
    // until it does. Rendering the denial during that window would flash "nemáte
    // oprávnění" at every legitimate cataloguer on every mount.
    if (isAuthorizationLoading) {
        // Same Paneset/Pane chrome as renderDenied — a bare Layout has no layout
        // container either, and the components mock renders Layout as a passthrough
        // div, so no test can see the difference.
        return React.createElement(ProposalGateLoading, null);
    }
    // We could not find out what the user may do. Every predicate reads false in
    // that state, so without this branch an authority admin would be told to go ask
    // for a role they already hold — the same "advice that can never work" this
    // ticket exists to remove. Still fail-closed; only the explanation differs.
    if (isAuthorizationError) {
        return renderUnavailable();
    }
    // Nothing this route can offer. Deliberately stricter than `canCreateProposal`
    // (which ProposalsList uses to decide whether to draw its button): that flag is
    // true for ANY authority-type role, including one held for a type this dialog
    // does not offer the caller. Such a user would otherwise get an empty grid and
    // no explanation. (Until CZNA-1087 the example here was CONSPECT_GROUP, as a
    // type the dialog never carried; it carries it now, gated per caller like every
    // other type, so it is no longer an example of anything.)
    if (creatableTypes.length === 0) {
        return renderDenied(null);
    }
    if (!selectedType) {
        return (React.createElement(AuthorityTypeDialog, { open: true, 
            // Offering a type the save would refuse is the same bug one step
            // earlier. An admin passes every type, so the grid is unchanged for them.
            allowedTypes: canCreateProposalOfType, onSelect: handleTypeSelect, onClose: function () { return history.push(DRAFTS_WORKSPACE); } }));
    }
    // A type can also arrive from the URL (`?type=`), which no dialog filtered —
    // the picker's propose-GA flow and a hand-typed link both land here.
    if (!canCreateProposalOfType(selectedType)) {
        return renderDenied(selectedType);
    }
    // CZNA-321: the picker-launched child of a publishing type is the minimal create.
    var minimalCreate = Boolean(flowToken) && PUBLISHING_PROPOSE_TARGETS.has(selectedType);
    // CZNA-321: one mount per flow step. The form reads its seed and initial values only
    // when it mounts, and a same-type child (PERSONAL founding a PERSONAL related person)
    // pushes this same route with the same props otherwise — so without a key the parent's
    // mounted form, values and all, would BECOME the child, and publishing it would publish
    // the parent. The return (`restore=`) is a new step for the same reason in reverse.
    var flowStepKey = flowToken ? "flow-".concat(flowToken) : (restoreToken ? "restore-".concat(restoreToken) : 'create');
    return (React.createElement(ProposalForm, { key: flowStepKey, recordType: selectedType, 
        // CZNA-456: create-time only. Nothing persists it, and the edit route
        // (ProposalEdit) deliberately passes no template, so every later view of
        // the record renders the complete form — the NK decision of 2026-08-04.
        templateId: selectedTemplateId, onCancel: handleCancel, 
        // Child GA create flow seeds the heading + hands the created proposal back
        // instead of navigating away itself.
        seedHeading: flowToken ? seedParam : undefined, onCreated: flowToken ? handleCreated : undefined, publishOnCreate: minimalCreate, visibleTabIds: minimalCreate ? MINIMAL_CREATE_TABS[selectedType] : undefined, 
        // Parent restore mount: rehydrate + write-back.
        restoreKey: restoreToken, returnListUrl: DRAFTS_WORKSPACE }));
};
export default ProposalCreate;
